import React, { useState } from "react";
import { auth } from "./firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { PublicPage } from "./components/public/PublicLayout";
import PasswordResetModal from "./components/public/PasswordResetModal";

export default function Login({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const [showResetModal, setShowResetModal] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        // --- Demo User Logic ---
        // For demonstration, allow login with a specific demo account
        // In a real application, you would remove this and rely solely on Firebase Auth
        if (email === "demo@loadmemo.com" && password === "password") {
            try {
                // Attempt to sign in the demo user via Firebase to get a valid session
                await signInWithEmailAndPassword(auth, email, password);
                if (onLogin) onLogin();
                navigate("/dashboard"); // Navigate to a dashboard route
            } catch (err) {
                 // If demo user doesn't exist in Firebase, handle it gracefully
                console.warn("Demo user not found in Firebase. Logging in locally for demo purposes.");
                if (onLogin) onLogin();
                navigate("/dashboard");
            } finally {
                setLoading(false);
            }
            return; // Stop execution for the demo user
        }
        // --- End Demo User Logic ---

        try {
            await signInWithEmailAndPassword(auth, email, password);
            if (onLogin) onLogin();
            navigate("/dashboard"); // Navigate to a dashboard route on successful login
        } catch (err) {
            switch (err.code) {
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                    setError("Wrong email or password.");
                    break;
                case 'auth/too-many-requests':
                    setError("Too many failed attempts. Reset your password or try again later.");
                    break;
                default:
                    setError("Something went wrong. Try again.");
                    console.error("Firebase Auth Error:", err);
                    break;
            }
        }
        setLoading(false);
    };

    const field = "pub-input w-full py-3.5 rounded-lg transition";

    return (
        <PublicPage>
            <section className="flex justify-center px-4 py-16 sm:py-24">
                <div className="pub-surface pub-line border rounded-2xl shadow-2xl shadow-black/30 p-8 sm:p-10 max-w-md w-full">
                    <h1 className="pub-text text-3xl sm:text-4xl font-extrabold tracking-tight">Sign in</h1>
                    <p className="pub-muted mt-2 text-lg">Manage your loads, drivers and pay.</p>

                    <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                        {error &&
                            <div role="alert" className="pub-error px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-3">
                                <i className="fas fa-circle-exclamation"></i>
                                <span>{error}</span>
                            </div>
                        }

                        <div>
                            <label htmlFor="email" className="pub-text block font-semibold mb-2">Email</label>
                            <div className="relative">
                                <i className="fas fa-envelope pub-faint absolute left-4 top-1/2 -translate-y-1/2"></i>
                                <input
                                    id="email"
                                    className={`${field} pl-12 pr-4`}
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                    placeholder="you@company.com"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label htmlFor="password" className="pub-text block font-semibold">Password</label>
                                <button
                                    type="button"
                                    onClick={() => setShowResetModal(true)}
                                    className="pub-link text-sm font-medium"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <div className="relative">
                                <i className="fas fa-lock pub-faint absolute left-4 top-1/2 -translate-y-1/2"></i>
                                <input
                                    id="password"
                                    className={`${field} pl-12 pr-12`}
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(s => !s)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    className="pub-faint absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:opacity-80"
                                >
                                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="w-full flex items-center justify-center gap-3 py-4 rounded-lg pub-btn text-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Signing in…
                                </>
                            ) : <>Sign in <i className="fas fa-arrow-right"></i></>}
                        </button>
                    </form>

                    <div className="pub-line pub-muted mt-7 pt-5 border-t text-center">
                        No account?{" "}
                        <button onClick={() => navigate('/register')} className="pub-link font-semibold">
                            Register
                        </button>
                    </div>
                </div>
            </section>

            {showResetModal && (
                <PasswordResetModal initialEmail={email} onClose={() => setShowResetModal(false)} />
            )}
        </PublicPage>
    );
}
